import { Link } from 'react-router-dom'
import Logo from '@/components/shared/Logo'

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <Logo size="lg" />
            </Link>

            <nav aria-label="Breadcrumb" className="hidden sm:block">
              <ol className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <li>
                  <Link to="/" className="hover:text-gray-700 dark:hover:text-gray-300">
                    Home
                  </Link>
                </li>
                <li aria-hidden>•</li>
                <li className="text-gray-700 dark:text-gray-300 font-medium">Privacy Policy</li>
              </ol>
            </nav>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pt-24">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">Privacy Policy</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Last updated: December 2025</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar Navigation */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                On this page
              </p>
              <nav className="space-y-2 text-sm">
                <a href="#info-collect" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600">
                  1. Information We Collect
                </a>
                <a href="#how-we-use" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600">
                  2. How We Use Your Information
                </a>
                <a href="#sharing" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600">
                  3. Information Sharing
                </a>
                <a href="#security" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600">
                  4. Data Security
                </a>
                <a href="#your-rights" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600">
                  5. Your Rights
                </a>
                <a href="#sms-communications" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600">
                  6. SMS Communications
                </a>
                <a href="#contact" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600">
                  7. Contact Us
                </a>
              </nav>
            </div>
          </aside>

          {/* Policy Content */}
          <section className="lg:col-span-9">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 sm:p-8 space-y-8">
              <section id="info-collect">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  1. Information We Collect
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  AsiliFlow collects information you provide directly to us, including:
                </p>
                <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-2">
                  <li>Name, email address, and contact information</li>
                  <li>Agency and organization details</li>
                  <li>Caregiver and client scheduling data</li>
                  <li>Shift notes and documentation</li>
                  <li>Account credentials and preferences</li>
                </ul>
              </section>

              <section id="how-we-use">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  2. How We Use Your Information
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-3">We use the information we collect to:</p>
                <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-2">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Process scheduling and shift management</li>
                  <li>Send technical notices and support messages</li>
                  <li>Respond to your comments and questions</li>
                  <li>Ensure compliance with healthcare regulations</li>
                  <li>Generate reports and analytics for your agency</li>
                </ul>
              </section>

              <section id="sharing">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  3. Information Sharing
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  We do not sell, trade, or rent your personal information to third parties. We may
                  share your information only in the following circumstances:
                </p>
                <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-2 mt-3">
                  <li>With your consent or at your direction</li>
                  <li>To comply with legal obligations</li>
                  <li>To protect rights, privacy, safety, or property</li>
                  <li>With service providers who assist in our operations</li>
                </ul>
              </section>

              <section id="security">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  4. Data Security
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  We implement appropriate technical and organizational measures to protect your
                  personal information against unauthorized access, alteration, disclosure, or
                  destruction. This includes encryption, secure servers, and regular security
                  assessments. AsiliFlow is designed to be HIPAA-compliant for healthcare data protection.
                </p>
              </section>

              <section id="your-rights">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  5. Your Rights
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-3">You have the right to:</p>
                <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-2">
                  <li>Access your personal information</li>
                  <li>Correct inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Object to processing of your information</li>
                  <li>Export your data in a portable format</li>
                </ul>
              </section>

              <section id="sms-communications">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  6. SMS Communications
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  AsiliFlow sends SMS text message notifications to registered caregivers and authorized family contacts who provide consent during account registration.
                </p>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">Message Types</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-2">
                  By consenting to SMS communications, you agree to receive the following types of messages:
                </p>
                <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-2">
                  <li>Open shift alerts and availability notifications</li>
                  <li>Shift reminders and schedule updates</li>
                  <li>Caregiver arrival and shift status notifications (for family contacts)</li>
                  <li>Schedule change confirmations</li>
                </ul>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">Opt-Out Instructions</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-2">
                  You may opt out of SMS communications at any time by:
                </p>
                <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-2">
                  <li>Replying STOP to any message</li>
                  <li>Updating your notification preferences in your account settings</li>
                  <li>Contacting us at support@getasili.com</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 mt-3">
                  Message frequency varies based on your schedule and shift availability. Standard message and data rates may apply depending on your mobile carrier plan.
                </p>
              </section>

              <section id="contact">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  7. Contact Us
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  If you have questions about this Privacy Policy or our data practices, please
                  contact us at:
                </p>
                <div className="mt-3 text-gray-700 dark:text-gray-300">
                  <p>
                    Email:{' '}
                    <a
                      href="mailto:support@getasili.com"
                      className="text-primary-600 hover:text-primary-700 underline underline-offset-2"
                    >
                      support@getasili.com
                    </a>
                  </p>
                </div>
              </section>

              {/* Back to top */}
              <div className="pt-2">
                <a
                  href="#"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600 rounded"
                >
                  ↑ Back to top
                </a>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-sm text-gray-500 dark:text-gray-400 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} AsiliFlow, a product of Asili LLC. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-gray-700 dark:hover:text-gray-300">
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-primary-600 hover:text-primary-700 font-medium">
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
